import { CabinetType, ShelvingType } from "building-types/casework/caseworkTypesEnum";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { PaletteType } from "building-types/materials/paletteType";
import type { CaseworkSettings } from "building-types/settings/caseworkSettings";
import {
  CLOSET_SHELF_DEPTH,
  CLOSET_SHELF_HEIGHT,
  COUNTER_DEPTH,
  COUNTER_HEIGHT,
  TALL_CABINET_HEIGHT,
} from "common/constants/buildingObjects";

/**
 * Default hardware position along a cabinet door, as a fraction of its height. Base and tall cabinets
 * put pulls near the top; upper cabinets near the bottom, so hardware sits within reach either way.
 */
export const HARDWARE_POSITION_LOWER_CABINET = 0.85;
const HARDWARE_POSITION_UPPER_CABINET = 0.15;

/**
 * Non-catalog casework renders without hardware, whatever `CaseworkSettings.hardware` says.
 *
 * Everything else below resolves from the settings, and `includeHardware` stays on
 * {@link ResolvedCaseworkShape} so the wiring is complete — generation reads it, the inspector can
 * show it, and turning it on later is this one constant. It is pinned off because reading the
 * setting here adds pulls to every non-catalog base/upper/tall cabinet in every existing doc: ~990
 * promotion face-diff cases across 169 fixtures. That is a geometry change, not a wiring change, so
 * it needs its own ticket and its own baseline sweep.
 *
 * Catalog casework is unaffected either way — it sets `includeHardware` from the settings itself in
 * `makeCasework`, and has done so since before this resolution path existed.
 */
const NON_CATALOG_INCLUDE_HARDWARE = false;

/**
 * A fully-resolved non-catalog casework shape. Every field is definite so generation never sees a
 * hole, even for the types that have no matching setting.
 */
export interface ResolvedCaseworkShape {
  readonly depth: number;
  readonly height: number;
  /**
   * Absolute offset from the entity's `verticalDatum`, matching how non-catalog casework has always
   * treated a doc `verticalOffset`. Deliberately *not* the delta-on-datum the catalog path applies
   * with `+=` — every existing non-catalog doc value is absolute, so switching to delta semantics
   * would double-count against the settings datum.
   */
  readonly verticalOffset: number;
  readonly material: PaletteMaterialSlot | undefined;
  readonly countertopMaterial: PaletteMaterialSlot | undefined;
  readonly paletteType: PaletteType | undefined;
  readonly includeHardware: boolean;
  readonly hardwareVerticalPositionParam: number;
}

/**
 * Settings-driven defaults for **non-catalog** casework of `caseworkType` — casework with no
 * `catalogDescriptorId`, whose dimensions used to be baked into the doc at creation time by
 * `caseworkCreationDefaults`.
 *
 * Pure by design: the caller supplies the already-resolved `CaseworkSettings`, and gates the call on
 * the Casework V2 flag itself. `packages/doc` must never read the flag registry — see
 * {@link ./caseworkFeatures}.
 *
 * Types with no matching setting fall back to the `common/constants/buildingObjects` constant that
 * `caseworkCreationDefaults` used to write, so those keep today's geometry.
 *
 * ## Known gap: upper cabinet sub-types are unreachable
 *
 * `upperMicrowaveCabinetDepth`, `upperRefrigeratorCabinetDepth`, `bottomOfMicrowaveUpperCabinets` and
 * `bottomOfRefrigeratorUpperCabinets` stay catalog-only, and so does the blind-corner panel.
 * `UpperCabinetSubType` lives on `CaseworkDescriptor`, not on `WallCaseworkBase`, so a non-catalog
 * upper cabinet has no sub-type to switch on and always takes the general values. Closing this needs
 * an optional `upperCabinetSubType` on `WallCaseworkFixed` plus an inspector row.
 */
export function resolveCaseworkFromSettings(
  caseworkType: CabinetType | ShelvingType | undefined,
  caseworkSettings: CaseworkSettings,
): ResolvedCaseworkShape {
  const { paletteType } = caseworkSettings;

  switch (caseworkType) {
    case CabinetType.BASE_CABINET:
      return {
        depth: caseworkSettings.baseCabinetDepth,
        height: caseworkSettings.topOfCountertop,
        verticalOffset: 0,
        material: caseworkSettings.baseCabinetMaterial,
        countertopMaterial: caseworkSettings.countertopMaterial,
        paletteType,
        includeHardware: NON_CATALOG_INCLUDE_HARDWARE,
        hardwareVerticalPositionParam: HARDWARE_POSITION_LOWER_CABINET,
      };
    case CabinetType.UPPER_CABINET:
      return {
        depth: caseworkSettings.upperCabinetDepth,
        // no upper cabinet height setting exists; the catalog path takes it from the descriptor
        height: COUNTER_HEIGHT,
        verticalOffset: caseworkSettings.bottomOfUpperCabinets,
        material: caseworkSettings.upperCabinetMaterial,
        countertopMaterial: undefined,
        paletteType,
        includeHardware: NON_CATALOG_INCLUDE_HARDWARE,
        hardwareVerticalPositionParam: HARDWARE_POSITION_UPPER_CABINET,
      };
    case CabinetType.TALL_CABINET:
      return {
        depth: caseworkSettings.tallCabinetDepth,
        // no tall cabinet height setting exists; the catalog path takes it from the descriptor
        height: TALL_CABINET_HEIGHT,
        verticalOffset: 0,
        material: caseworkSettings.tallCabinetMaterial,
        // tall cabinets are countertop-less: generation passes countertopThickness: 0
        countertopMaterial: undefined,
        paletteType,
        includeHardware: NON_CATALOG_INCLUDE_HARDWARE,
        hardwareVerticalPositionParam: HARDWARE_POSITION_LOWER_CABINET,
      };
    case CabinetType.FILLER_PANEL:
      return {
        // filler panels follow the base cabinet box, matching the catalog path
        depth: caseworkSettings.baseCabinetDepth,
        height: caseworkSettings.topOfCountertop,
        verticalOffset: 0,
        material: caseworkSettings.baseCabinetMaterial,
        countertopMaterial: undefined,
        paletteType,
        // a filler panel has no doors, so no hardware — the catalog path gives it none either
        includeHardware: false,
        hardwareVerticalPositionParam: HARDWARE_POSITION_LOWER_CABINET,
      };
    case ShelvingType.CLOSET_SHELF:
      return {
        // no closet shelf depth setting exists; the catalog path uses the descriptor's shelfDepth
        depth: CLOSET_SHELF_DEPTH,
        height: CLOSET_SHELF_HEIGHT,
        // topOfUpperShelf is the shelf *top*, so the bottom sits one shelf thickness below it
        verticalOffset: caseworkSettings.topOfUpperShelf - CLOSET_SHELF_HEIGHT,
        // no closet shelf material setting exists
        material: undefined,
        countertopMaterial: undefined,
        paletteType: undefined,
        includeHardware: false,
        hardwareVerticalPositionParam: HARDWARE_POSITION_LOWER_CABINET,
      };
    case undefined:
      // Generic casework has no type to resolve from, so it keeps the legacy constants. This is why
      // `caseworkCreationDefaults` still writes them explicitly for the generic case even with
      // Casework V2 on — leave that asymmetry alone.
      return {
        depth: COUNTER_DEPTH,
        height: COUNTER_HEIGHT,
        verticalOffset: 0,
        material: undefined,
        countertopMaterial: undefined,
        paletteType: undefined,
        includeHardware: false,
        hardwareVerticalPositionParam: HARDWARE_POSITION_LOWER_CABINET,
      };
  }
}

/** The subset of a casework entity that {@link resolveCaseworkShape} treats as overrides. */
export interface CaseworkShapeOverrides {
  readonly caseworkType: CabinetType | ShelvingType | undefined;
  readonly depth?: number;
  readonly height?: number;
  readonly verticalOffset?: number;
  readonly material?: PaletteMaterialSlot;
  readonly paletteType?: PaletteType;
}

/**
 * Applies a casework entity's explicit doc values on top of the settings defaults. An absent field
 * means "inherit from Casework Settings"; a present one is an override the user typed.
 *
 * Use this from generation. Use {@link resolveCaseworkFromSettings} directly where you want the
 * inherited value alone — the inspector needs it to show as a placeholder.
 *
 * Covers **non-catalog** casework only. A catalog item's dimensions and material come from its
 * descriptor and the settings inside `makeCasework`, which applies the same explicit-material-wins rule
 * against `inheritsMaterial`; this function still runs first for it, and the catalog branch overwrites
 * what it resolved.
 */
export function resolveCaseworkShape(
  { caseworkType, depth, height, verticalOffset, material, paletteType }: CaseworkShapeOverrides,
  caseworkSettings: CaseworkSettings,
): ResolvedCaseworkShape {
  const fromSettings = resolveCaseworkFromSettings(caseworkType, caseworkSettings);

  return {
    ...fromSettings,
    depth: depth ?? fromSettings.depth,
    height: height ?? fromSettings.height,
    verticalOffset: verticalOffset ?? fromSettings.verticalOffset,
    material: material ?? fromSettings.material,
    // Non-catalog casework has always painted its countertop with the cabinet's own material. An
    // entity carrying an explicit material keeps that, so no existing doc re-renders; only one that
    // inherits picks up the dedicated countertop setting.
    countertopMaterial: material ?? fromSettings.countertopMaterial,
    // A PaletteMaterialSlot is only meaningful within its palette type, so an inherited material has
    // to bring the settings palette with it or an exterior slot gets read against the interior one.
    paletteType: material !== undefined ? paletteType : fromSettings.paletteType ?? paletteType,
  };
}
